import {
  Archive,
  Copy,
  MoreVertical,
  RefreshCw,
  Star,
  Trash2,
  Users,
} from "lucide-react";
import React, { useState } from "react";
import { toast } from "sonner";
import { usePermissions } from "../../hooks/usePermissions";
import { useScenarios } from "../../hooks/useScenarios";
import { cn } from "../../lib/utils";
import type { ComprehensiveScenario } from "../../types/scenarios";
import { ParkScenarioModal } from "../modals/ScenarioModals";
import { Button } from "../ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import { DeleteConfirmModal, DeleteErrorModal } from "./ScenarioActionModals";
import {
  resolveFreshness,
  ScenarioFreshnessBadge,
} from "./ScenarioFreshnessBadge";
import { ViewApproversModal } from "./ViewApproversModal";

interface ScenarioActionsMenuProps {
  scenario: ComprehensiveScenario;
  onClone?: (id: string) => void;
  /** When true, render the freshness badge to the left of the 3-dot trigger. */
  showFreshnessBadge?: boolean;
  className?: string;
}

/**
 * Shared 3-dot actions menu for scenarios (Clone, Favourite, View Approvers,
 * Refresh, Park, Delete) plus its confirmation modals. Used by both the tiled
 * ScenarioCard and the scenario list row so behaviour and gating stay in sync.
 * Clicks are stopped from propagating so they never trigger row/card navigation.
 */
export const ScenarioActionsMenu: React.FC<ScenarioActionsMenuProps> = ({
  scenario,
  onClone,
  showFreshnessBadge = false,
  className,
}) => {
  const { toggleFavorite, deleteScenario, refreshScenario } = useScenarios();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission("create_scenarios");

  const [deleteErrorModal, setDeleteErrorModal] = useState(false);
  const [deleteConfirmModal, setDeleteConfirmModal] = useState(false);
  const [parkModal, setParkModal] = useState(false);
  const [viewApproversModal, setViewApproversModal] = useState(false);

  // Permission-based checks from currentUserPermission
  const permission = scenario.currentUserPermission;
  const hasEditPermission = permission === "owner" || permission === "edit";
  const hasOwnerPermission = permission === "owner";

  // Handle Delete action
  const handleDelete = () => {
    // Cannot delete active scenarios
    if (scenario.status === "active") {
      setDeleteErrorModal(true);
      return;
    }
    setDeleteConfirmModal(true);
  };

  const confirmDelete = () => {
    deleteScenario(scenario.id);
    setDeleteConfirmModal(false);
    toast.success(`"${scenario.name}" has been deleted`);
  };

  // Handle Park action — available from any non-parked status.
  // Opens the rich ParkScenarioModal so a reason and review date are always collected.
  const handlePark = () => {
    if (scenario.status === "parked") return;
    setParkModal(true);
  };

  const handleClone = () => {
    if (onClone) {
      onClone(scenario.id);
    }
  };

  const handleToggleFavorite = () => {
    toggleFavorite(scenario.id);
  };

  // Freshness / refresh gating — parked & archived scenarios are never refreshed
  const freshness = resolveFreshness(scenario);
  const isUpdating = freshness === "updating";
  const isParkedOrArchived =
    scenario.status === "parked" || scenario.status === "archived";
  const canRefresh = hasEditPermission && !isParkedOrArchived;

  const handleRefresh = () => {
    if (!canRefresh || isUpdating) return;
    refreshScenario(scenario.id);
    toast.success(`Refreshing "${scenario.name}"`);
  };

  return (
    <>
      <div
        className={cn("flex items-center gap-2", className)}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {showFreshnessBadge && <ScenarioFreshnessBadge scenario={scenario} />}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-52">
            {canCreate && (
              <DropdownMenuItem onClick={() => handleClone()}>
                <Copy className="h-4 w-4 mr-2" />
                Clone
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={() => handleToggleFavorite()}>
              <Star className="h-4 w-4 mr-2" />
              {scenario.isFavorite ? "Remove from" : "Add to"} Favourites
            </DropdownMenuItem>
            {scenario.status === "pending-approval" && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => setViewApproversModal(true)}>
                  <Users className="h-4 w-4 mr-2" />
                  View Approvers
                </DropdownMenuItem>
              </>
            )}
            {canRefresh && (
              <DropdownMenuItem
                onClick={() => handleRefresh()}
                disabled={isUpdating}
              >
                <RefreshCw
                  className={cn("h-4 w-4 mr-2", isUpdating && "animate-spin")}
                />
                {isUpdating ? "Refreshing…" : "Refresh"}
              </DropdownMenuItem>
            )}
            <DropdownMenuSeparator />
            {hasEditPermission && scenario.status !== "parked" && (
              <DropdownMenuItem onClick={() => handlePark()}>
                <Archive className="h-4 w-4 mr-2" />
                Park
              </DropdownMenuItem>
            )}
            {hasOwnerPermission &&
              (scenario.status === "draft" || scenario.status === "parked") && (
                <DropdownMenuItem
                  onClick={() => handleDelete()}
                  className="text-neutral-700"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete
                </DropdownMenuItem>
              )}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      {/* Modals */}
      <DeleteErrorModal
        open={deleteErrorModal}
        onClose={() => setDeleteErrorModal(false)}
        status={scenario.status}
        scenarioName={scenario.name}
      />
      <DeleteConfirmModal
        open={deleteConfirmModal}
        onClose={() => setDeleteConfirmModal(false)}
        onConfirm={confirmDelete}
        scenarioName={scenario.name}
      />
      {parkModal && (
        <ParkScenarioModal
          scenario={scenario}
          onClose={() => setParkModal(false)}
          onParked={() => toast.success(`"${scenario.name}" has been parked`)}
        />
      )}
      <ViewApproversModal
        open={viewApproversModal}
        onClose={() => setViewApproversModal(false)}
        scenario={scenario}
      />
    </>
  );
};
